/* ========================================================================== *\
	IMPORTS
\* ========================================================================== */
import { bind, isElement, merge } from 'lodash';

import {
	addListener,
	removeListener,
	dispatchCustomEvent
} from '../../01-helpers/event-manager';



/* ========================================================================== *\
	PRIVATE VARIABLES
\* ========================================================================== */

const
	moduleName = 'navigator',
	baseJsClass = `js-${ moduleName }`,

	navigationDelay = 300,

	direction = Object.freeze({
		back: 'back',
		forward: 'forward'
	}),

	eventName = Object.freeze({
		/**
		 * This event will be dispatched after the visitor has stopped
		 * interacting with the navigator for a set number of milliseconds.
		 */
		navigationFinished: 'navigation-finished',
		/**
		 * This event will be dispatched when the user starts navigating using
		 * the widget. It will be dispatched before the navigated event and it
		 * can be cancelled. When it is cancelled no navigation will occur.
		 */
		navigationStart: 'navigation-start',
		/**
		 * This event will dispatched after the navigationStart event. It can be
		 * dispatched multiple times before the navigationFinished event
		 * is dispatched.
		 */
		navigated: 'navigated',
		/**
		 * This event will be dispatched when the position of the navigator has
		 * been changed through code and not by the user interacting with
		 * the widget.
		 */
		positionChanged: 'position-changed'
	}),

	propertyKey = {
		baseElement: Symbol('baseElement'),
		canGoBack: Symbol('canGoBack'),
		canGoForward: Symbol('canGoForward'),
		isIdle: Symbol('isNavigating'),
		finishTimeout: Symbol('finishTimeout')
	},

	selector = {
		triggerPrevious: `.${ baseJsClass }-previous`,
		triggerNext: `.${ baseJsClass }-next`
	};



/* ========================================================================== *\
	PRIVATE METHODS
\* ========================================================================== */
/**
 * @this NavigatorBase
 */
function setup() {
	const
		triggerPrevious = this.baseElement.querySelector(selector.triggerPrevious),
		triggerNext = this.baseElement.querySelector(selector.triggerNext);

	if (triggerPrevious !== null) {
		addListener(triggerPrevious, 'click', bind(onNavigateBack, this));
		this[propertyKey.canGoBack] = !triggerPrevious.disabled;
	}

	if (triggerNext !== null) {
		addListener(triggerNext, 'click', bind(onNavigateForward, this));
		this[propertyKey.canGoForward] = !triggerNext.disabled;
	}
}

/**
 *
 * @param {Event} event
 *
 * @this NavigatorBase
 */
function onNavigateBack(event) {
	if (!this.canGoBack) {
		return;
	}

	navigate.call(this, direction.back);
}

/**
 *
 * @param {Event} event
 *
 * @this NavigatorBase
 */
function onNavigateForward(event) {
	if (!this.canGoForward) {
		return;
	}

	navigate.call(this, direction.forward);
}



/* ========================================================================== *\
	PRIVATE METHODS
\* ========================================================================== */

/**
 * @param {string} direction
 *
 * @this NavigatorBase
 */
function isAllowedToNavigate(direction) {
	const event = dispatchCustomEvent(this.baseElement, eventName.navigationStart, {
		direction
	}, {
		cancelable: true
	});

	return !event.defaultPrevented;
}

/**
 * Handles the navigation.
 *
 * @param {string} direction
 *
 * @this NavigatorBase
 */
function navigate(direction) {
	// When this navigator is idle dispatch the start event. When the event is
	// cancelled do not proceed with navigation.
	if (
		this.isIdle &&
		!isAllowedToNavigate.call(this, direction)
	) {
		return;
	}

	this[propertyKey.isIdle] = false;
	// Clear the timeout that may have been set to dispatch the event that the
	// navigator has become idle again.
	clearTimeout(this[propertyKey.finishTimeout]);

	// Call the preprocess method. This gives subclasses a chance to do some
	// housekeeping before the navigate event is dispatched.
	this._preprocessNavigate(direction);

	// Send the navigated event to inform others the user has navigated either
	// forward or back.
	dispatchCustomEvent(this.baseElement, eventName.navigated, {
		direction
	});

	// Set the timeout after which the navigation action is considered done.
	this[propertyKey.finishTimeout] = scheduleNavigationFinishedEvent.call(this);
}

/**
 * @returns {number} The ID of the timeout after which the navigation finished
 *          event will be dispatched.
 *
 * @this NavigatorBase
 */
function scheduleNavigationFinishedEvent() {
	return setTimeout(() => {
		// Reset the flag, the navigation action has finished.
		this[propertyKey.isIdle] = true;
		// Dispatch the event to inform others the navigation is done.
		dispatchCustomEvent(this.baseElement, eventName.navigationFinished);
	}, navigationDelay);
}

/**
 *
 * @param {string} selector
 * @param {boolean} isEnabled
 *
 * @this NavigatorBase
 */
function updateButtonState(selector, isEnabled) {
	const
		button = this.baseElement.querySelector(selector);
	if (button === null) {
		return;
	}

	button.disabled = !isEnabled;
}


/* ========================================================================== *\
	PUBLIC API
\* ========================================================================== */

class NavigatorBase {
	/* ====================================================================== *\
		CONSTRUCTOR
	\* ====================================================================== */
	/**
	 *
	 * @param {HTMLElement} baseElement
	 */
	constructor(baseElement) {
		if (!isElement(baseElement)) {
			throw 'Unable to create instance of Navigator, no base element has been provided.';
		}

		this[propertyKey.baseElement] = baseElement;
		this[propertyKey.isIdle] = true;
		setup.call(this);
	}



	/* ====================================================================== *\
		STATIC PROPERTIES
	\* ====================================================================== */
	/**
	 * @enum
	 *
	 * @readonly
	 * @static
	 * @memberof GenericNavigator
	 */
	static get direction() {
		return direction;
	}

	/**
	 * @enum {NavigatorEvents}
	 *
	 * @readonly
	 * @static
	 * @memberof Navigator
	 */
	static get events() {
		return eventName;
	}



	/* ====================================================================== *\
		INSTANCE PROPERTIES
	\* ====================================================================== */

	/* ---------------------------------- *\
		baseElement (r/o)
	\* ---------------------------------- */
	/**
	 * Returns the element which was used to create an instance of Navigator.
	 *
	 * @type {HTMLElement}
	 *
	 * @readonly
	 * @memberof Navigator
	 */
	get baseElement() {
		return this[propertyKey.baseElement];
	}

	/* ---------------------------------- *\
		canGoBack
	\* ---------------------------------- */
	/**
	 * @type {boolean}
	 *
	 * @memberof Navigator
	 */
	get canGoBack() {
		return this[propertyKey.canGoBack];
	}
	set canGoBack(value) {
		value = !!value;
		if (value === this.canGoBack) {
			return;
		}

		this[propertyKey.canGoBack] = value;
		updateButtonState.call(this, selector.triggerPrevious, value);
	}

	/* ---------------------------------- *\
		canGoForward
	\* ---------------------------------- */
	/**
	 * @type {boolean}
	 *
	 * @memberof Navigator
	 */
	get canGoForward() {
		return this[propertyKey.canGoForward];
	}
	set canGoForward(value) {
		value = !!value;
		if (value === this.canGoForward) {
			return;
		}

		this[propertyKey.canGoForward] = value;
		updateButtonState.call(this, selector.triggerNext, value);
	}

	/* ---------------------------------- *\
		isNavigating (r/o)
	\* ---------------------------------- */
	/**
	 * Returns whether or not the user is in the process of navigating to a new
	 * state.
	 *
	 * @type {boolean}
	 *
	 * @readonly
	 * @memberof Navigator
	 */
	get isIdle() {
		return this[propertyKey.isIdle];
	}



	/* ====================================================================== *\
		PROTECTED METHODS
	\* ====================================================================== */
	_dispatchInstanceEvent(eventName, detail = null) {
		const
			eventDetail = merge({}, detail, { instance: this });
		dispatchCustomEvent(this.baseElement, eventName, eventDetail);
	}

	/**
	 * @param {NavigationDirection} direction
	 *
	 * @protected
	 */
	_preprocessNavigate(direction) {
		// Override this method to handle custom behaviour for navigation.
	}



	/* ====================================================================== *\
		CALLBACK REGISTRATION
	\* ====================================================================== */

	register(name, callback) {
		addListener(this.baseElement, name, callback);
	}

	unregister(name, callback) {
		removeListener(this.baseElement, name, callback);
	}
}



/* ========================================================================== *\
	EXPORTS
\* ========================================================================== */

export default NavigatorBase;
